// © 2026 AttainIP Engineering
/**
 * Labels for the Select-Organisation picker (RFC-0510 W28).
 *
 * Identity fills a choice's `name`/`org_ext_id` in only when it can resolve the
 * organisation, so a choice may arrive carrying nothing but a UUID. The picker's
 * job is different from the active-organisation indicator's (`activeOrgLabel` in
 * `store/auth`), and so is its failure mode:
 *
 *  - The indicator FAILS CLOSED to showing nothing, because a wrong label on a
 *    safety badge is worse than no badge.
 *  - The picker CANNOT show nothing. A human who cannot tell two rows apart
 *    cannot sign in at all. So it degrades to a short, obviously-technical
 *    "Organisation <first 8 of the id>" rather than pretending the UUID is a
 *    name — and never renders a bare full UUID as the primary label.
 *
 * W29 adds the thing that made the degradation necessary in the first place: a
 * PERSONAL organisation has no name and no readable ext_id, so W28 could only
 * offer it as "Organisation ce75cb74" — pickable, and still not a choice a human
 * can make. It is now labelled from `org_type`, which Identity reads off the
 * organisation record.
 *
 * ⚠️ THE TYPE IS READ, NEVER DEDUCED. The tempting rule — "no name and no
 * ext_id means personal" — is a guess, and the day an enterprise organisation
 * exists without a name it would be labelled CONFIDENTLY WRONG, which is worse
 * than a UUID. So the only thing that produces the personal label is
 * `org_type === 'personal'`. A choice whose type is missing (an Identity that
 * predates W29 — the field is additive) or unrecognised is not assumed to be
 * either kind: it falls straight back to W28's name → ext_id → short-id chain.
 *
 * W39 adds `orgIdentity`, a STRICTER sibling for the "acting in" control and the
 * active-organisation indicator — the two places a wrong or absent answer is a
 * safety problem rather than a cosmetic one. Same resolution chain, different
 * failure: it never degrades to something that reads like a name. It also adds
 * the placeholder guard both need — Identity writes `Organization (ce75cb74)`
 * into the `name` column for an entra-tenant organisation it could not name, and
 * that is not a UUID, so the existing guard let it through.
 *
 * Kept free of store/config imports on purpose: it is a leaf a widely-mounted
 * component pulls in.
 */

/** The shape of one choice from `OrgSelectionRequiredResponse.organizations`. */
export interface OrgChoiceLike {
  organization_id: string;
  org_ext_id?: string | null;
  name?: string | null;
  /**
   * The organisation's own type, verbatim from Identity's `OrgType` vocabulary
   * (`personal` | `domain` | `entra_tenant` | `enterprise_domain`). Additive
   * (RFC-0510 W29): absent means NOT KNOWN, never a kind. Widened to `string`
   * on purpose, so a value this build has never heard of degrades to the
   * fallback label instead of failing to typecheck or being coerced.
   */
  org_type?: string | null;
}

/** Identity's `OrgType.PERSONAL` — the one type with a label of its own. */
const PERSONAL_ORG_TYPE = 'personal';

/**
 * What a human is choosing when they pick their own single-user organisation.
 *
 * Deliberately NOT "selfserve" or "Self-serve": that is our internal
 * `account_type` value, and to a user it names a PRICING TIER rather than the
 * thing they are choosing between. `selfserve` stays the field value everywhere
 * it already is; this is the only string a human reads.
 */
export const PERSONAL_ORG_LABEL = 'Personal workspace';

/** Is this choice a personal organisation? Only a POSITIVE match counts. */
export function isPersonalOrgChoice(choice: OrgChoiceLike): boolean {
  return choice.org_type === PERSONAL_ORG_TYPE;
}

/** A canonical UUID — `organization_id`, and the `ext_id` of entra-tenant orgs
 *  (a raw Azure tenant GUID). Neither is a human-readable organisation name. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The readable half of an `ext_id`, or `null` when there isn't one.
 *
 * ext_ids are `"<prefix>:<readable>"` (e.g. `"enterprise-domain:hpdlaw.com"` →
 * `"hpdlaw.com"`). Entra-tenant orgs have no prefix and a raw GUID — rejected.
 */
export function readableOrgExtId(extId?: string | null): string | null {
  const trimmed = extId?.trim();
  if (!trimmed) return null;
  const colon = trimmed.indexOf(':');
  const readable = (colon >= 0 ? trimmed.slice(colon + 1) : trimmed).trim();
  if (!readable || UUID_RE.test(readable)) return null;
  return readable;
}

/**
 * Identity's own placeholder for an organisation it could not name —
 * `Organization (ce75cb74)`, or `Organization (default)` when there was not
 * even a tenant id (`user_use_cases.py`, the entra-tenant branch).
 *
 * ⚠️ IT LIVES IN THE `name` COLUMN, which is why it needs catching here: it is
 * not a UUID, so the guard above lets it through, and it is not a name, so
 * printing it tells a human nothing except that we do not know. RFC-0510 W29's
 * backfill correctly declined to invent names and left this in place; a control
 * a human uses WHILE WORKING must say "unresolved" rather than pass it off.
 *
 * Deliberately anchored and narrow — it matches Identity's exact construction,
 * not anything that merely starts with "Organization". A real firm called
 * "Organization (Ireland) Ltd" keeps its name.
 */
const PLACEHOLDER_ORG_NAME_RE = /^Organi[sz]ation \((?:[0-9a-f]{8}|default)\)$/i;

/** Is this the placeholder Identity writes when it cannot name an org? */
export function isPlaceholderOrgName(name?: string | null): boolean {
  const trimmed = name?.trim();
  return !!trimmed && PLACEHOLDER_ORG_NAME_RE.test(trimmed);
}

/** A name we may show a human as an organisation's name, or `null`. */
function realOrgName(name?: string | null): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  if (UUID_RE.test(trimmed)) return null;
  if (isPlaceholderOrgName(trimmed)) return null;
  return trimmed;
}

/** The primary label for a choice. Always non-empty — see the module note. */
export function orgChoiceLabel(choice: OrgChoiceLike): string {
  // The type first, because it is the only thing that is actually KNOWN about a
  // personal organisation — its name is absent and its ext_id is `personal:<uuid>`.
  if (isPersonalOrgChoice(choice)) return PERSONAL_ORG_LABEL;

  const name = realOrgName(choice.name);
  if (name) return name;

  const readable = readableOrgExtId(choice.org_ext_id);
  if (readable) return readable;

  return `Organisation ${choice.organization_id.slice(0, 8)}`;
}

/**
 * What a human reads when an organisation could not be named — RFC-0510 W39.
 *
 * Said out loud rather than shown as an absence: under the acting-in control
 * "nothing" would be indistinguishable from "my own firm", and an attorney who
 * cannot tell whose tenancy they are in has a confidentiality problem, not a
 * cosmetic one.
 */
export const UNRESOLVED_ORG_LABEL = 'Unknown organisation';

/** A resolved organisation identity, or an honest statement that there isn't one. */
export interface OrgIdentity {
  /** Always non-empty, and safe to render. */
  label: string;
  /** False when `label` is `UNRESOLVED_ORG_LABEL` — nothing could be resolved. */
  resolved: boolean;
  /**
   * A short technical marker (`ID ce75cb74`) for an UNRESOLVED entry, so two of
   * them can still be told apart and quoted to support. `null` when resolved,
   * and never offered as though it were a name.
   */
  hint: string | null;
}

/**
 * The organisation identity for the "acting in" control (RFC-0510 W39).
 *
 * A stricter sibling of `orgChoiceLabel`, for the two places where a wrong or
 * absent answer is a safety problem rather than a cosmetic one: the
 * active-organisation indicator, and the switcher's rows.
 *
 * The difference is the FAILURE, not the success. Where a name (or a readable
 * domain) exists, both produce it. Where none does, `orgChoiceLabel` degrades
 * to `Organisation ce75cb74` — pickable, at the login screen, where the human
 * is choosing between rows they already know. This one refuses to imply that
 * anything was resolved: it says `Unknown organisation` and carries the short
 * id as a separate, obviously-technical hint. A GUID in a login picker is bad;
 * a GUID in a control someone uses while working is worse.
 */
export function orgIdentity(choice: OrgChoiceLike): OrgIdentity {
  if (isPersonalOrgChoice(choice)) {
    return { label: PERSONAL_ORG_LABEL, resolved: true, hint: null };
  }

  const name = realOrgName(choice.name);
  if (name) return { label: name, resolved: true, hint: null };

  const readable = readableOrgExtId(choice.org_ext_id);
  if (readable) return { label: readable, resolved: true, hint: null };

  return {
    label: UNRESOLVED_ORG_LABEL,
    resolved: false,
    hint: `ID ${choice.organization_id.slice(0, 8)}`,
  };
}

/**
 * A secondary line for a choice, or `null` when it would only repeat the
 * primary. Two firms can share a display name; the domain is what tells them
 * apart, so it is shown whenever it adds something.
 */
export function orgChoiceSubLabel(choice: OrgChoiceLike): string | null {
  // A personal organisation has no domain, and its ext_id (`personal:<uuid>`)
  // is an identifier, not a second line worth reading.
  if (isPersonalOrgChoice(choice)) return null;

  const readable = readableOrgExtId(choice.org_ext_id);
  if (!readable) return null;
  return readable === orgChoiceLabel(choice) ? null : readable;
}
